import * as Crypto from 'expo-crypto';
import {Platform} from 'react-native';
import {smgEProtectUrl, smgPayPageId, smgReportGroup} from '../constants';
import {EProtectResponse} from '../types';

type CardTokenizationErrorCode = | 'UNSUPPORTED_PLATFORM' | 'CONFIGURATION_ERROR' | 'SDK_LOAD_FAILED'
  | 'SDK_LOAD_TIMEOUT' | 'SDK_UNAVAILABLE' | 'TOKENIZATION_FAILED' | 'TOKENIZATION_TIMEOUT';

type EProtectRequest = { paypageId: string, reportGroup: string, orderId: string, id: string, url: string };
type EProtectFields = { accountNum: HTMLInputElement, cvv: HTMLInputElement };
type EProtectFailure = { response?: string };
type EProtectClient = {
  sendToEprotect: (request: EProtectRequest, fields: EProtectFields, onSuccess: (response: EProtectResponse) => void, onError: (response: EProtectFailure) => void, onTimeout: () => void, timeout: number) => void
};
type EProtectConstructor = new () => EProtectClient;
type EProtectWindow = Window & { eProtect?: EProtectConstructor };

const sdkScriptId = 'evenplay-eprotect-sdk';
const sdkLoadTimeoutMs = 15000;
const tokenizationTimeoutMs = 15000;
const tokenizationSafetyTimeoutMs = tokenizationTimeoutMs + 5000;

export const cardTokenizationPreparationErrorMessage = 'Secure card entry could not be prepared. Please try again.';

export const isCardTokenizationSupported = () => Platform.OS === 'web';

let sdkLoadPromise: Promise<void> | undefined;

class CardTokenizationError extends Error {
  constructor(public readonly code: CardTokenizationErrorCode, message: string) {
    super(message);
    this.name = 'CardTokenizationError';
  }
}

const preparationErrorCodes = new Set<CardTokenizationErrorCode>([
  'UNSUPPORTED_PLATFORM', 'CONFIGURATION_ERROR', 'SDK_LOAD_FAILED', 'SDK_LOAD_TIMEOUT', 'SDK_UNAVAILABLE'
]);

const getErrorCode = (error: unknown): CardTokenizationErrorCode | undefined => {
  const code = (error as { code?: unknown })?.code;
  return typeof code === 'string' ? code as CardTokenizationErrorCode : undefined;
};

export const isCardTokenizationPreparationError = (error: unknown) => {
  const code = getErrorCode(error);
  return !!code && preparationErrorCodes.has(code);
};

export const buildSafeCardTokenizationErrorLog = (error: unknown) => ({code: getErrorCode(error) || 'UNKNOWN'});

const getConfig = () => {
  const url = typeof smgEProtectUrl === 'string' ? smgEProtectUrl.trim().replace(/\/$/, '') : '';
  const paypageId = typeof smgPayPageId === 'string' ? smgPayPageId.trim() : '';
  const reportGroup = typeof smgReportGroup === 'string' ? smgReportGroup.trim() : '';

  if (!url || !paypageId || !reportGroup) {
    throw new CardTokenizationError('CONFIGURATION_ERROR', cardTokenizationPreparationErrorMessage);
  }

  return {url, paypageId, reportGroup};
};

const getEProtectConstructor = () => {
  const EProtect = (window as EProtectWindow).eProtect;
  return typeof EProtect === 'function' ? EProtect : undefined;
};

const removeSdkScript = () => {
  if (typeof document === 'undefined') return;
  document.getElementById(sdkScriptId)?.remove();
};

export const prepareCardTokenization = (): Promise<void> => {
  if (!isCardTokenizationSupported() || typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new CardTokenizationError('UNSUPPORTED_PLATFORM', 'Card tokenization is not available on this platform yet.'));
  }

  let url: string;
  try {
    ({url} = getConfig());

  } catch (error) {
    return Promise.reject(error);
  }

  if (getEProtectConstructor()) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  const pending = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(sdkScriptId) as HTMLScriptElement | null;
    const script = existingScript || document.createElement('script');
    let settled = false;

    const cleanup = () => {
      clearTimeout(timeout);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
    const fail = (error: CardTokenizationError) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      reject(error);
    };
    const handleLoad = () => {
      if (settled) return;
      if (!getEProtectConstructor()) {
        fail(new CardTokenizationError('SDK_UNAVAILABLE', cardTokenizationPreparationErrorMessage));
        return;
      }

      settled = true;
      cleanup();
      resolve();
    };
    const handleError = () => {
      fail(new CardTokenizationError('SDK_LOAD_FAILED', cardTokenizationPreparationErrorMessage));
    };
    const timeout = setTimeout(() => {
      fail(new CardTokenizationError('SDK_LOAD_TIMEOUT', cardTokenizationPreparationErrorMessage));
    }, sdkLoadTimeoutMs);

    script.addEventListener('load', handleLoad, {once: true});
    script.addEventListener('error', handleError, {once: true});

    if (!existingScript) {
      script.id = sdkScriptId;
      script.async = true;
      script.src = `${url}/eProtect/eProtect-api3.js`;
      document.head.appendChild(script);
    }
  });

  sdkLoadPromise = pending;
  void pending.catch(() => {
    if (sdkLoadPromise === pending) sdkLoadPromise = undefined;
  });
  return pending;
};

export const retryCardTokenizationPreparation = () => {
  sdkLoadPromise = undefined;
  removeSdkScript();
  return prepareCardTokenization();
};

const getEProtectErrorMessage = (response: EProtectFailure = {}) => {
  const messages: Record<string, string> = {
    871: 'Invalid card number. Check and retry. (Not Mod10)', 872: 'Invalid card number. Check and retry. (Too short)',
    873: 'Invalid card number. Check and retry. (Too long)',
    874: 'Invalid card number. Check and retry. (Not a number)',
    875: 'We are experiencing technical difficulties. Please try again later or contact support.',
    876: 'Invalid card number. Check and retry. (Failure from Server)',
    881: 'Invalid card validation code. Check and retry. (Not a number)',
    882: 'Invalid card validation code. Check and retry. (Too short)',
    883: 'Invalid card validation code. Check and retry. (Too long)',
    889: 'We are experiencing technical difficulties. Please try again later or contact support.'
  };
  return messages[response.response || ''] || 'Unable to verify this card. Please check the details and try again.';
};

const createTokenizationFields = (number: string, code: string) => {
  const container = document.createElement('div');
  const accountNum = document.createElement('input');
  const cvv = document.createElement('input');

  container.setAttribute('aria-hidden', 'true');
  container.style.position = 'absolute';
  container.style.width = '1px';
  container.style.height = '1px';
  container.style.overflow = 'hidden';
  container.style.opacity = '0';
  container.style.pointerEvents = 'none';

  accountNum.autocomplete = 'off';
  accountNum.tabIndex = -1;
  accountNum.value = number;
  cvv.autocomplete = 'off';
  cvv.tabIndex = -1;
  cvv.value = code;
  container.append(accountNum, cvv);
  document.body.appendChild(container);

  return {
    fields: {accountNum, cvv},
    cleanup: () => {
      accountNum.value = '';
      cvv.value = '';
      container.remove();
    }
  };
};

export const tokenizeCard = async (number: string, code: string): Promise<EProtectResponse> => {
  await prepareCardTokenization();
  const EProtect = getEProtectConstructor();
  if (!EProtect) {
    throw new CardTokenizationError('SDK_UNAVAILABLE', cardTokenizationPreparationErrorMessage);
  }

  const {url, paypageId, reportGroup} = getConfig();
  const {fields, cleanup} = createTokenizationFields(number, code);
  const request = {
    paypageId, reportGroup, url,
    orderId: Crypto.randomUUID().replaceAll('-', '').slice(0, 24),
    id: Crypto.randomUUID().replaceAll('-', '').slice(0, 24)
  };

  return new Promise<EProtectResponse>((resolve, reject) => {
    let settled = false;
    let safetyTimeout: ReturnType<typeof setTimeout>;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(safetyTimeout);

      try {
        cleanup();

      } finally {
        callback();
      }
    };

    const rejectTimeout = () => finish(() => reject(new CardTokenizationError(
      'TOKENIZATION_TIMEOUT',
      'We are experiencing technical difficulties. Please try again later or contact support. (timeout)'
    )));
    safetyTimeout = setTimeout(rejectTimeout, tokenizationSafetyTimeoutMs);

    try {
      new EProtect().sendToEprotect(
        request,
        fields,
        response => finish(() => resolve(response)),
        response => finish(() => reject(new CardTokenizationError(
          'TOKENIZATION_FAILED', getEProtectErrorMessage(response)
        ))),
        rejectTimeout,
        tokenizationTimeoutMs
      );

    } catch {
      finish(() => reject(new CardTokenizationError(
        'TOKENIZATION_FAILED', 'Unable to verify this card. Please check the details and try again.'
      )));
    }
  });
};
